import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import Nav from "@/components/Nav";
import Seo from "@/components/Seo";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Lock, CheckCircle } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (pw !== pw2) { setError("Passwords don't match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (er) {
      setError(formatApiErrorDetail(er.response?.data?.detail) || "Reset failed");
    }
    setBusy(false);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-linen">
        <Seo title="Reset password" noindex />
        <Nav />
        <div className="max-w-md mx-auto px-5 py-16 text-center">
          <h1 className="font-serif text-3xl text-forest">Missing reset link</h1>
          <p className="text-forest/70 mt-3">Please request a new password reset link.</p>
          <Link to="/forgot-password" className="btn-primary mt-6 inline-block">Request reset link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-linen">
      <Seo title="Choose a new password" path="/reset-password" noindex />
      <Nav />
      <div className="max-w-md mx-auto px-5 py-16">
        {done ? (
          <div className="text-center" data-testid="reset-done">
            <div className="w-16 h-16 rounded-full bg-sage/25 border border-sage/40 mx-auto flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-forest" />
            </div>
            <h1 className="font-serif text-3xl text-forest mt-6">Password updated</h1>
            <p className="text-forest/75 mt-3">Redirecting you to sign in…</p>
          </div>
        ) : (
          <>
            <div className="w-12 h-12 rounded-full bg-sage/25 flex items-center justify-center">
              <Lock className="w-5 h-5 text-forest" />
            </div>
            <h1 className="font-serif text-4xl text-forest mt-5">Choose a new password</h1>
            <p className="text-forest/70 mt-2">At least 6 characters. Make it something you'll remember.</p>
            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-form">
              <div>
                <label className="text-[11px] tracking-[0.12em] uppercase text-forest/60">New password</label>
                <input type="password" required minLength={6} autoComplete="new-password"
                  value={pw} onChange={(e) => setPw(e.target.value)} className="input-organic mt-1" data-testid="reset-pw" />
              </div>
              <div>
                <label className="text-[11px] tracking-[0.12em] uppercase text-forest/60">Confirm password</label>
                <input type="password" required minLength={6} autoComplete="new-password"
                  value={pw2} onChange={(e) => setPw2(e.target.value)} className="input-organic mt-1" data-testid="reset-pw2" />
              </div>
              {error && <p className="text-sm text-red-600" data-testid="reset-error">{error}</p>}
              <button disabled={busy} className="btn-primary w-full disabled:opacity-60" data-testid="reset-submit">
                {busy ? "Updating…" : "Update password"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
